import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import EventLocalizedText from 'components/EventLocalizedText';
import { currenciesMap } from 'lib/constants/currencies';
import { TransactionItem } from 'lib/graphql/entities/financialReport/types';
import { EventWaitingLine } from 'lib/graphql/entities/waitingLines/types';
import { DateTime } from 'luxon';
import LanguageContext from 'providers/i18n/LanguageContext';
import {
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import useTransactionsQuery from './useTransactionsQuery';

function formatAmount(amount: number, currency: string) {
  const formattedCurrency = currency.toUpperCase();
  const symbol = currenciesMap[formattedCurrency].symbol || '$';
  return `${(amount / 100).toFixed(2)}${symbol} ${formattedCurrency}`;
}

interface TransactionsGridProps {
  selectedTime: string
  selectedWaitingLine: string
  waitingLinesMap: Record<string, EventWaitingLine>
}

function TransactionsGrid(props: TransactionsGridProps) {
  const { selectedTime, selectedWaitingLine, waitingLinesMap } = props;

  const { formatMessage } = useIntl();
  const { language } = useContext(LanguageContext);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  });

  const [
    transactions,
    {
      fetchMore,
      hasNextPage,
      loading,
    },
  ] = useTransactionsQuery(selectedTime, selectedWaitingLine);

  const onPageChange = useCallback(async (model: GridPaginationModel) => {
    const newItemCount = (model.page + 1) * model.pageSize;
    if (transactions && newItemCount > transactions.length) {
      await fetchMore();
    }
    setPaginationModel(model);
  }, [fetchMore, transactions]);

  const columns: GridColDef<TransactionItem>[] = useMemo(() => [
    {
      headerName: formatMessage({ id: 'date' }),
      field: 'timestamp',
      flex: 1,
      valueFormatter: (params) => DateTime.fromISO(params, { zone: 'UTC-0' }).setLocale(language).toFormat('D'),
    },
    {
      headerName: formatMessage({ id: 'transaction' }),
      field: 'id',
      flex: 2,
    },
    {
      headerName: formatMessage({ id: 'waiting_line' }),
      field: 'waitingLineId',
      flex: 1,
      renderCell: ({ row }) => {
        const { waitingLineId } = row;
        return <EventLocalizedText text={waitingLinesMap[waitingLineId]?.name} />;
      },
    },
    {
      headerName: formatMessage({ id: 'sale_price' }),
      field: 'totalAmount',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (_, row) => formatAmount(row.totalAmount, row.currency),
    },
    {
      headerName: formatMessage({ id: 'stripe_fees' }),
      field: 'feeAmount',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (_, row) => formatAmount(row.feeAmount, row.currency),
    },
    {
      headerName: formatMessage({ id: 'taxes' }),
      field: 'taxAmount',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (_, row) => formatAmount(row.taxAmount, row.currency),
    },
    {
      headerName: formatMessage({ id: 'balance' }),
      field: 'eventAmount',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ row }) => (
        <b>{formatAmount(row.eventAmount, row.currency)}</b>
      ),
    },
  ], [formatMessage, language, waitingLinesMap]);

  const visibleRows = useMemo(() => {
    const { page, pageSize } = paginationModel;
    if (!transactions) return [];
    return transactions.slice(page * pageSize, (page + 1) * pageSize);
  }, [paginationModel, transactions]);

  return (
    <DataGrid
      loading={loading}
      rows={visibleRows}
      rowCount={hasNextPage ? -1 : transactions?.length || 0}
      columns={columns}
      pagination
      paginationModel={paginationModel}
      paginationMode="server"
      onPaginationModelChange={onPageChange}
      pageSizeOptions={[10]}
    />
  );
}

export default TransactionsGrid;
